import React, { useState, useContext } from 'react';
import Spinner from '../common/spinner/Spinner';
import { useUserActions } from '../user/UserActionProvider';
import TextButton from '../common/button/TextButton';
import AddToHomescreen from '../AddToHomescreen/AddToHomescreen';
import Translate from '../locale/Translate';
import BrowserChecker from '../browserChecker/BrowserChecker';
import LinkButton from '../common/button/LinkButton';
import Modal, { ModalSection } from '../common/modal/Modal';
import Varsel from '../varsel/Varsel';
import { isFeatureEnabled, ENABLE_VARSEL } from '../../utils/FeatureFlagUtils';
import { UserContext } from '../user/UserProvider';
import { useAuth } from 'react-oidc-context';
import { getAndroidTWAStatus } from '../../utils/urlUtils';

const LandingPage = (): JSX.Element => {
  const { toggleCreateUser } = useUserActions();
  const auth = useAuth();
  const { login } = useContext(UserContext);

  const tilgjengelighetserklaeringClick = (): void => {
    window
      ?.open('https://uustatus.no/nb/erklaringer/publisert/6a9f31a6-4834-4192-a043-f9e22d4d5af0', '_blank', 'noopener')
      ?.focus();
  };

  const ProcessFQA = (chunks: any[]): JSX.Element => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
      <>
        <TextButton className="text-left" onClick={() => setIsModalOpen(!isModalOpen)}>
          {chunks}
        </TextButton>
        {isModalOpen && (
          <Modal
            isOpen={isModalOpen}
            toggle={() => {
              setIsModalOpen(!isModalOpen);
            }}
            preventScroll
          >
            <ModalSection>
              <>
                <h3 className="my-3 heading-3">
                  <Translate id="innmelding-bekreft.faq.header" />
                </h3>
                <div className="w-full my-3 text-3 tablet:text-2">
                  <Translate id="innmelding-bekreft.faq.section1" />
                </div>
                <div className="w-full my-3 text-3 tablet:text-2">
                  <Translate id="innmelding-bekreft.faq.section2" />
                </div>
                <div className="w-full my-3 text-3 tablet:text-2">
                  <Translate id="innmelding-bekreft.faq.section3" />
                </div>
              </>
            </ModalSection>
          </Modal>
        )}
      </>
    );
  };

  if (auth.isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <BrowserChecker />
      <div className="flex flex-col items-stretch justify-center pt-[60px] pb-5 text-2">
        <div className="desktop:mx-auto max-w-[800px]">
          {!getAndroidTWAStatus() ? (
            <>
              <h1 className="text-center heading-1">
                <Translate id="landing.header" fallback="Velkommen til Bymelding" />
              </h1>
              {isFeatureEnabled(ENABLE_VARSEL) && <Varsel />}
              <div className="mt-3 mx-3 tablet:mx-[80px] desktop:mx-0 text-left">
                <p className="mt-3">
                  <Translate
                    id="landing.paragraph"
                    fallback="Her kan du raskt og enkelt melde fra om feil og mangler."
                  />
                </p>
                <p className="sr-only">
                  <Translate
                    id="landing.infoScreenReaders"
                    values={{
                      contactEmail: () => (
                        <a
                          className=""
                          href="mailto:postmottak@bym.oslo.kommune.no?subject=Bymelding&body=Beskriv feilen/mangelen så presist du kan, legg gjerne ved bilde.%0D%0ABeskriv hvor feilen/mangelen befinner seg så presist du kan, det holder ikke med kun adresse. Gjelder henvendelsen f.eks. hull i veien må vi vite nøyaktig hvor dette hullet befinner seg, ikke kun hvilken gate.%0D%0A"
                        >
                          postmottak@bym.oslo.kommune.no
                        </a>
                      ),
                      contactPhone: () => <a href="tel:23 48 20 30">23 48 20 30</a>,
                    }}
                  ></Translate>
                </p>
                <p className="mt-3">
                  <Translate
                    id="landing.whatHappensWhenSubmitted"
                    fallback="Hva skjer når du sender inn en melding?"
                    values={{
                      ProcessFQA: ProcessFQA,
                    }}
                  />
                </p>
                {!auth.isAuthenticated && (
                  <p className="mt-3">
                    <Translate
                      id="landing.login_paragraph"
                      fallback=""
                      values={{
                        loginLink: (chunks: any[]) => <TextButton onClick={login}>{chunks}</TextButton>,
                        registerLink: (chunks: any[]) => <TextButton onClick={toggleCreateUser}>{chunks}</TextButton>,
                      }}
                    />
                  </p>
                )}
              </div>
              <div className="flex justify-center my-5 flex-center">
                <LinkButton to="/innmelding/kategori" className="w-[300px]">
                  <Translate id="landing.startNewButton" fallback="Meld sak" />
                </LinkButton>
              </div>

              <div className="flex justify-center my-5 flex-center">
                <p className="p-3 text-center text-4">
                  <Translate
                    id="landing.tilgjengelighetserklaering"
                    fallback=""
                    values={{
                      tilgjengelighetserklaering: (chunks: any[]) => (
                        <TextButton onClick={tilgjengelighetserklaeringClick}>{chunks}</TextButton>
                      ),
                    }}
                  />
                </p>
              </div>
            </>
          ) : (
            <>
              <h1 className="text-center heading-1">Appen Bymelding er deaktivert</h1>
              <div className="mt-3 mx-3 tablet:mx-[80px] desktop:mx-0 text-left">
                <p className="mt-3">
                  Appen Bymelding ble deaktivert for mobilbrukere i juni 2021 på grunn av tekniske utfordringer. Det
                  enkleste er å legge <a href="https://bymelding.no">https://bymelding.no</a> som snarvei på
                  hjem-skjermen din. Du vil da få et Bymelding-ikon på telefonen din sammen med alle de andre appene
                  dine og det vil oppleves som om du benytter en helt vanlig app.
                </p>
                <p className="mt-3">
                  Bymelding vil finne nøyaktig lokasjon på kartet dersom du har aktivert stedstjenester på telefonen.
                  Ved å logge på med brukernavn og passord vil all historikk følge med fra appen over på
                  nettleserversjonen.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <AddToHomescreen />
    </>
  );
};

export default LandingPage;
